import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase/client'
import { useAuth } from './AuthContext'

type UserRole = 'cashier' | 'manager' | 'admin'

// Granular clerk permission flags (POS1.tblClerkPermissions) + module flags.
type PermFlags = Record<string, boolean | number>

interface RoleContextType {
  role: UserRole | null
  loading: boolean
  flags: PermFlags
  hasPermission: (permission: string) => boolean
  // True if the signed-in user may perform a granular clerk action. Managers and
  // admins always pass (legacy "Manager" role had every permission); cashiers
  // pass only when their specific flag is enabled.
  can: (flag: string) => boolean
  // Numeric limit (e.g. perm_max_refund_amount); admins/managers => Infinity.
  limit: (key: string) => number
}

const RoleContext = createContext<RoleContextType | undefined>(undefined)

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [role, setRole] = useState<UserRole | null>(null)
  const [flags, setFlags] = useState<PermFlags>({})
  const [loading, setLoading] = useState(true)

  const fetchUserRole = useCallback(async () => {
    if (!user) {
      setRole(null)
      setFlags({})
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data: roleData, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (error) {
        console.error('Error fetching user role:', error)
        setRole(null)
        setFlags({})
      } else {
        setRole((roleData?.role as UserRole) || null)
        const f: PermFlags = {}
        if (roleData) for (const k of Object.keys(roleData)) {
          if (k.startsWith('perm_') || k.startsWith('module_')) f[k] = roleData[k] as boolean | number
        }
        setFlags(f)
      }
    } catch (error) {
      console.error('Error in fetchUserRole:', error)
      setRole(null)
      setFlags({})
    } finally {
      setLoading(false)
    }
  }, [user])

  // Re-fetch role whenever the authenticated user changes (e.g. after login).
  useEffect(() => {
    if (authLoading) return
    fetchUserRole()
  }, [authLoading, fetchUserRole])

  const hasPermission = (permission: string): boolean => {
    if (!role) return false
    
    // Define permissions per role
    const permissions: Record<UserRole, string[]> = {
      cashier: [
        'process_sales',
        'accept_payments',
        'print_receipts',
        'customer_lookup',
        'basic_refunds',
      ],
      manager: [
        'process_sales',
        'accept_payments',
        'print_receipts',
        'customer_lookup',
        'basic_refunds',
        'full_refunds',
        'inventory_manage',
        'vendor_manage',
        'basic_reports',
        'price_overrides',
      ],
      admin: [
        'process_sales',
        'accept_payments',
        'print_receipts',
        'customer_lookup',
        'basic_refunds',
        'full_refunds',
        'inventory_manage',
        'vendor_manage',
        'basic_reports',
        'price_overrides',
        'advanced_reports',
        'user_manage',
        'system_config',
        'financial_reports',
        'multi_store',
      ],
    }

    return (permissions[role as UserRole] ?? []).includes(permission)
  }

  const can = (flag: string): boolean => {
    if (role === 'admin' || role === 'manager') return true
    return flags[flag] === true
  }

  const limit = (key: string): number => {
    if (role === 'admin' || role === 'manager') return Infinity
    const v = flags[key]
    return typeof v === 'number' ? v : 0
  }

  return (
    <RoleContext.Provider value={{ role, loading, flags, hasPermission, can, limit }}>
      {children}
    </RoleContext.Provider>
  )
}

export function useRole() {
  const context = useContext(RoleContext)
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider')
  }
  return context
}
